import { useState, useRef, useEffect } from "react";
import { FileText, Image, FileSpreadsheet, File, Presentation, MoreVertical, Trash2, ExternalLink, Pencil, Link2, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { SharepointFile } from "@/features/files/types/sharepoint";
import { formatFileSize } from "@/shared/utils/fileHelpers";

interface FileCardProps {
  file: SharepointFile;
  isOfficer: boolean;
  onView: (fileId: string) => void;
  onDelete: (fileId: string) => void;
  onRename?: (file: SharepointFile) => void;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith("image/")) return { Icon: Image, color: "text-green-600", bg: "bg-green-50" };
  if (mimeType === "application/pdf") return { Icon: FileText, color: "text-red-600", bg: "bg-red-50" };
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel"))
    return { Icon: FileSpreadsheet, color: "text-emerald-600", bg: "bg-emerald-50" };
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint"))
    return { Icon: Presentation, color: "text-orange-600", bg: "bg-orange-50" };
  if (mimeType.includes("word") || mimeType === "application/msword")
    return { Icon: FileText, color: "text-blue-600", bg: "bg-blue-50" };
  return { Icon: File, color: "text-gray-600", bg: "bg-gray-50" };
}

export const FileCard = ({ file, isOfficer, onView, onDelete, onRename }: FileCardProps) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { Icon, color, bg } = getFileIcon(file.file_type);

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const link = `${window.location.origin}/files/${file.folder_id}?file=${file.id}`;
    await navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => {
      setLinkCopied(false);
      setMenuOpen(false);
    }, 1200);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const formattedDate = new Date(file.created_at).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div
      className="group relative bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer"
      onClick={() => onView(file.id)}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 ${bg} rounded-lg shrink-0`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-900 truncate text-sm">{file.display_name}</p>
          <p className="text-xs text-gray-500 mt-1">
            {formatFileSize(file.file_size)} &middot; {formattedDate}
          </p>
        </div>

        <div ref={menuRef} className="relative shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            className="p-1 rounded hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="w-4 h-4 text-gray-500" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  onView(file.id);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <ExternalLink className="w-4 h-4" />
                {t("sharepoint.open")}
              </button>
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                {linkCopied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
                {linkCopied ? t("sharepoint.link_copied") : t("sharepoint.copy_link")}
              </button>
              {isOfficer && onRename && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onRename(file);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Pencil className="w-4 h-4" />
                  {t("sharepoint.rename")}
                </button>
              )}
              {isOfficer && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onDelete(file.id);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {t("sharepoint.delete")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
